import React from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  GlobalStyles,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import {styled} from '@mui/material/styles';
import {GridColDef, GridSortModel} from '@mui/x-data-grid';
import moment from 'moment';

import {Images} from '../../../configs/images';
import {Image} from '../../../components/atoms/Image';
import {DataTable} from '../../../components/molecules/DataTable';

const GreenSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#008545',
    '&:hover': {
      backgroundColor: 'rgba(0, 133, 69, 0.08)',
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#7fc4a4',
    opacity: 0.55,
  },
}));

export const HEADER_ANALYSIS_MIN_ROW_HEIGHT = 76;

const capitalizeFirstLetter = (text = '') => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const THREAT_SCORE_LEVEL_LABELS: Record<string, string> = {
  clean: 'No Threat',
  threat: 'Threat Detected',
  undetermined: 'Undetermined',
};

const getThreatScoreLevelLabel = (level = '') =>
  THREAT_SCORE_LEVEL_LABELS[level] ?? capitalizeFirstLetter(level);

const tooltipSx = {
  bgcolor: '#011111',
  borderRadius: '4px',
  fontSize: '13px',
  fontWeight: 500,
  maxWidth: '700px',
  p: '8px',
};

const ThreatScoreTooltipContent = ({row}: {row: any}) => {
  const actions = row.rawRule?.actions || {};

  return (
    <Box sx={{fontSize: '13px', lineHeight: 1.2}}>
      <Box>Threat assessment:</Box>
      <Box component="ul" sx={{m: 0, pl: '20px'}}>
        <li>Threat Level: {getThreatScoreLevelLabel(row.level)}</li>
        <li>Threat Score: {row.threatScoreContribution}</li>
      </Box>
      <Box sx={{mt: 2}}>Then set:</Box>
      <Box component="ul" sx={{m: 0, pl: '20px'}}>
        <li>Set Priority: {actions.priority || '-'}</li>
        <li>Set Category: {actions.category || '-'}</li>
        <li>Set Process Status: {actions.process_status || '-'}</li>
      </Box>
    </Box>
  );
};

interface HeaderAnalysisRulesTableProps {
  data: any[];
  checked: Record<string, boolean>;
  isReadOnlyRule: (row: any) => boolean;
  changeStatusRule: (status: any, row: any) => void;
  onOpenActions: (anchor: HTMLElement, ruleId: any) => void;
  onRowDoubleClick: (params: any) => void;
  paginationComponent: () => React.ReactElement;
  loadingTable: boolean;
  onSortModelChange: (model: any) => void;
  sortModel: GridSortModel;
}

export const HeaderAnalysisRulesTable: React.FC<HeaderAnalysisRulesTableProps> = ({
  data,
  checked,
  isReadOnlyRule,
  changeStatusRule,
  onOpenActions,
  onRowDoubleClick,
  paginationComponent,
  loadingTable,
  onSortModelChange,
  sortModel,
}) => {
  const columns: GridColDef[] = [
    {
      field: 'ruleName',
      headerName: 'Rule Name',
      flex: 1,
      minWidth: 240,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => (
          <Tooltip
              title={params.value}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    alignItems: "center",
                    maxWidth: "680px",
                    borderRadius: "4px",
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '8px',
                    marginLeft: '54px',
                    textOverflow: 'initial',
                  },
                },
              }}
          >
        <span
            className="MuiDataGrid-cellContent"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
        >
          {params.value}
        </span>
          </Tooltip>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 240,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => (
        <Tooltip
            title={params.value}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  alignItems: "center",
                  maxWidth: "680px",
                  borderRadius: "4px",
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                  fontSize: '13px',
                  fontWeight: '500',
                  padding: '8px',
                  textOverflow: 'initial',
                },
              },
            }}
        >
        <Box
            className="header-analysis-description-cell"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
        >
          {params.value}
        </Box>
        </Tooltip>
      ),
    },
    {
      field: 'emailAuthentication',
      headerName: 'Condition Checks',
      width: 240,
      minWidth: 240,
      sortable: false,
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params)=>{
        return(
          <Box
              className="header-analysis-email-checks-list"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                lineHeight: "160%",
                py: "8px"
              }}
          >
            {params.value?.map((item :any, index: number) => (
              <Tooltip
                  key={item.id || index}
                  title="Check if these Email Authentication Checks fail"
                  arrow
                  componentsProps={{tooltip: {sx: tooltipSx}}}
              >
               <Box
                  className="header-analysis-email-check-chip"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    alignSelf: "flex-start",
                    gap: 1,
                    px: "10px",
                    py: "4px",
                    bgcolor: "#F2F3F3",
                    borderRadius: "6px",
                    fontWeight: 700,
                  }}
              >
                <Typography
                    variant="body2"
                    sx={{
                      color: "#000000E5",
                      fontWeight: 700,
                      fontSize: 12,
                      lineHeight: 1.6,
                    }}
                >
                  {item.name} › {item.type}
                </Typography>

              </Box>
              </Tooltip>
            ))}
          </Box>
        )
      }
    },
    {
      field: 'threatScoreContribution',
      headerName: 'Threat Score',
      width: 200,
      minWidth: 200,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderHeader: () => (
        <Box sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '6px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#212121'
        }}>
          Threat Score
          <Tooltip
              title={'Format: [Email Threat Level] \u203a [Email Threat Score]'}
              arrow
              placement='top'
              componentsProps={{tooltip: {sx: tooltipSx}}}
          >
            <HelpIcon
                aria-label="Threat Score format information"
                tabIndex={0}
                sx={{cursor: 'pointer', fontSize: '16px'}}
                htmlColor="#193051"
            />
          </Tooltip>
        </Box>
      ),
      renderCell:(row:any)=>{
        return (
          <Tooltip
              title={<ThreatScoreTooltipContent row={row.row}/>}
              arrow
              componentsProps={{tooltip: {sx: tooltipSx}}}
          >
            <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                }}
                className={`header-analysis-threat-score-chip ${row.row.level}`}
            >
              <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                  }}
              >
                {getThreatScoreLevelLabel(row.row.level)} ›
              </Typography>

              <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                  }}
              >
                {row.value}
              </Typography>
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => (
        <Box
            className="header-analysis-last-updated-cell"
            sx={{display: "flex", flexDirection: "column", fontSize: 14, fontWeight: 400, lineHeight: 1.6}}
        >
          <span>{moment(params.value).format('DD/MM/YYYY')}</span>
          <span>{moment(params.value).format('hh:mm A')}</span>
        </Box>
      ),
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      width: 160,
      minWidth: 160,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow>
          <Typography
              className="header-analysis-added-by-value"
              variant="body2"
              fontWeight={700}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
          >
            {params.value || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 125,
      minWidth: 125,
      sortable: false,
      cellClassName: (row: any) => {
        return checked[row.id] ? '' : 'custom-disable'
      },
      renderCell: (params) => {
        const isCustom = params.value === 'Custom';
        return (
          <Chip
              label={params.value}
              className={isCustom
                ? 'header-analysis-type-chip header-analysis-type-chip-custom'
                : 'header-analysis-type-chip header-analysis-type-chip-default'}
              sx={{
                border: isCustom ? '1px solid #A3C622' : '1px solid #557499',
                bgcolor: isCustom ? '#F1FACD' : '#E7EBF0',
                color: "#000000",
                height: "30px",
                minWidth: "93px",
                borderRadius: "4px",
                fontWeight: 500,
                fontSize: 14,
                '& .MuiChip-label': {
                  px: "20px",
                  py: "5px",
                  overflow: "visible",
                },
              }}
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 134,
      sortingOrder: ['asc', 'desc'],
      minWidth: 134,
      maxWidth: 134,
      renderCell: (row: any) => {
        const readOnly = isReadOnlyRule(row.row);
        return <FormControlLabel className={`status-col ${checked[row.id] ? 'active' : 'inactive'}`} control={
          <GreenSwitch checked={checked[row.id]}
                       disabled={readOnly}
                       onClick={(e) => {
            e.stopPropagation();
          }}
                       onChange={(status: any) => changeStatusRule(status, row)}/>
        } label={checked[row.id] ? 'Active' : 'Inactive'}/>
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 79,
      minWidth: 79,
      maxWidth: 79,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',       
      headerAlign: 'center',
      renderCell: (params) => (
        !isReadOnlyRule(params.row) && <IconButton
          sx={{width:24, height:24}}
          data-testid="header-analysis-row-actions"
          onClick={(event) => {
            event.stopPropagation();
            onOpenActions(event.currentTarget, params.id as any);
          }}
        >
          <Image cssClass="image-style image-action" srcImg={Images.MoreHorizontal} margin="0"/>
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          '.header-analysis-datatable .MuiDataGrid-cell': {
            alignItems: 'center',
          },
          '.header-analysis-datatable .MuiDataGrid-row': {
            minHeight: `${HEADER_ANALYSIS_MIN_ROW_HEIGHT}px !important`,
          },
        }}
      />
      <DataTable
        columns={columns}
        data={data}
        onRowDoubleClick={onRowDoubleClick}
        pagination
        CustomPagination={paginationComponent}
        loadingTable={loadingTable}
        onSortModelChange={onSortModelChange}
        sortModel={sortModel}
        rowHeight={'auto' as any}
      />
    </>
  );
};
